import React, {useState, useCallback} from 'react';
import {useMutation} from '@tanstack/react-query';
import {AdminService} from '@/services/adminService';
import Card from '@/components/common/Card';

interface Props {
    className?: string;
}

interface OrderSearchFields {
    oid: string;
    firstName: string;
    lastName: string;
    email: string;
}

interface OrderResult {
    oid: string;
    firstName: string;
    lastName: string;
    email: string;
    birthdate: string;
    status: string;
}

interface Toast {
    error: boolean;
    message: string;
}

/**
 * OrderSearch component - Admin page to search for orders in the database
 * Refactored to follow docs/SUMMARY.md best practices
 */
export const OrderSearch: React.FC<Props> = () => {
    const [searchFields, setSearchFields] = useState<OrderSearchFields>({
        oid: '',
        firstName: '',
        lastName: '',
        email: '',
    });

    const [results, setResults] = useState<OrderResult[]>([]);

    const [toast, setToast] = useState<Toast>({
        error: false,
        message: '',
    });

    // React Query mutation for order search
    const searchMutation = useMutation({
        mutationFn: (searchFields: OrderSearchFields) => AdminService.searchOrders(searchFields),
        onSuccess: (data) => {
            // Process the data to format birthdate like the old component
            const processedData = data.map((order: any) => ({
                ...order,
                birthdate: order.birthdate ? order.birthdate.split('T')[0] : null,
            }));

            if (processedData.length === 0) {
                setToast({
                    error: false,
                    message: 'No Order Found.',
                });
            } else {
                setToast({error: false, message: ''});
            }
            setResults(processedData);
        },
        onError: (error) => {
            console.error('Order search error:', error);
            setToast({
                error: true,
                message: 'Error searching orders. Please try again.',
            });
            setResults([]);
        },
    });

    const handleInputChange = useCallback(
        (field: keyof OrderSearchFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchFields((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
        },
        []
    );

    const handleSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            setToast({error: false, message: ''});

            searchMutation.mutate(searchFields);
        },
        [searchFields, searchMutation]
    );

    const formatDate = useCallback((dateString: string) => {
        if (!dateString) return '';
        return dateString.split('T')[0];
    }, []);

    return (
        <div className="space-y-6">
            <Card>
                <Card.Header>
                    <Card.Title>Search for orders in database</Card.Title>
                </Card.Header>
                <Card.Content>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label
                                    htmlFor="searchOrders-oid"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    Order ID
                                </label>
                                <input
                                    id="searchOrders-oid"
                                    type="text"
                                    value={searchFields.oid}
                                    placeholder="Optional"
                                    onChange={handleInputChange('oid')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="searchOrders-fname"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    First Name
                                </label>
                                <input
                                    id="searchOrders-fname"
                                    type="text"
                                    value={searchFields.firstName}
                                    placeholder="Optional"
                                    onChange={handleInputChange('firstName')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="searchOrders-lname"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Name
                                </label>
                                <input
                                    id="searchOrders-lname"
                                    type="text"
                                    value={searchFields.lastName}
                                    placeholder="Optional"
                                    onChange={handleInputChange('lastName')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="searchOrders-email"
                                    className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <input
                                    id="searchOrders-email"
                                    type="email"
                                    value={searchFields.email}
                                    placeholder="Optional"
                                    onChange={handleInputChange('email')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                type="submit"
                                disabled={searchMutation.isPending}
                                className="px-4 py-2 bg-mld-primary text-white rounded-md hover:bg-mld-primary/90 focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                {searchMutation.isPending ? 'Searching...' : 'Search Orders'}
                            </button>

                            {toast.message && (
                                <div
                                    className="px-3 py-2 rounded-md text-sm text-white"
                                    style={{backgroundColor: toast.error ? '#F15E87' : '#42D4B0'}}>
                                    {toast.message}
                                </div>
                            )}
                        </div>
                    </form>
                </Card.Content>
            </Card>

            {results.length > 0 && (
                <Card>
                    <Card.Header>
                        <Card.Title>Search Results ({results.length} orders found)</Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Order ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            First Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Birthday
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {results.map((order: OrderResult, index) => (
                                        <tr key={order.oid || index} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {order.oid}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {order.firstName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {order.lastName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {order.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatDate(order.birthdate)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className="inline-flex px-2 py-1 text-xs font-medium rounded-full text-white"
                                                    style={{
                                                        backgroundColor:
                                                            order.status === 'completed'
                                                                ? '#42D4B0'
                                                                : order.status === 'pending'
                                                                  ? '#FFBE7A'
                                                                  : '#6B7280',
                                                    }}>
                                                    {order.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card.Content>
                </Card>
            )}
        </div>
    );
};

export default OrderSearch;
